<script setup>
import {
  computed,
  ref,
  useTemplateRef,
  onMounted,
  onUnmounted,
  nextTick,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementSize } from '@vueuse/core';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['modeChanged']);

const store = useStore();
const { t } = useI18n();

const autoModeRef = useTemplateRef('autoModeRef');
const manualModeRef = useTemplateRef('manualModeRef');
const dropdownRef = ref(null);
const itemRefs = ref([]);

const autoModeSize = useElementSize(autoModeRef);
const manualModeSize = useElementSize(manualModeRef);

const showTimeoutDropdown = ref(false);

const conversation = computed(() => store.getters.getSelectedChat);
const isManualMode = computed(() => conversation.value?.agent_mode === 0);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isAgentBotFeatureEnabled = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, 'agent_bots')
);

// Get the inbox's configured timeout setting
const inboxTimeout = computed(() => {
  const inboxId = conversation.value?.inbox_id;
  if (!inboxId) return 'never';

  const inbox = store.getters['inboxes/getInbox'](inboxId);
  return inbox?.agent_mode_timeout || 'never';
});

// Get current timeout setting from conversation or inbox default
const currentTimeout = computed(() => {
  const duration = conversation.value?.agent_mode_timeout_duration;
  // Check if duration is defined and not null (0 is valid for 'never')
  if (duration !== undefined && duration !== null) {
    // Convert to number strictly
    const seconds = Number(duration);
    if (seconds === 0) return 'never';
    if (seconds <= 300) return '5min';
    if (seconds <= 600) return '10min';
    if (seconds <= 900) return '15min';
    if (seconds <= 1800) return '30min';
    if (seconds <= 3600) return '1hr';
    if (seconds <= 14400) return '4hr';
    if (seconds <= 86400) return '1day';
    if (seconds <= 604800) return '1week';
  }
  return inboxTimeout.value;
});

// Check if timeout is scheduled (not 'never')
const hasScheduledTimeout = computed(() => {
  return (
    conversation.value?.agent_mode_timeout_at !== null &&
    conversation.value?.agent_mode_timeout_at !== undefined
  );
});

// Timeout options for dropdown
// Timeout options for dropdown
const timeoutOptions = computed(() => [
  { value: 'never', label: t('AGENT_MODE.TIMEOUT.OPTION_NEVER') },
  { value: '5min', label: t('AGENT_MODE.TIMEOUT.OPTION_5MIN') },
  { value: '10min', label: t('AGENT_MODE.TIMEOUT.OPTION_10MIN') },
  { value: '15min', label: t('AGENT_MODE.TIMEOUT.OPTION_15MIN') },
  { value: '30min', label: t('AGENT_MODE.TIMEOUT.OPTION_30MIN') },
  { value: '1hr', label: t('AGENT_MODE.TIMEOUT.OPTION_1HR') },
  { value: '4hr', label: t('AGENT_MODE.TIMEOUT.OPTION_4HR') },
  { value: '1day', label: t('AGENT_MODE.TIMEOUT.OPTION_1DAY') },
  { value: '1week', label: t('AGENT_MODE.TIMEOUT.OPTION_1WEEK') },
]);

const width = computed(() => {
  const widthToUse = isManualMode.value
    ? manualModeSize.width.value
    : autoModeSize.width.value;
  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

const translateValue = computed(() => {
  const xTranslate = isManualMode.value ? autoModeSize.width.value + 16 : 0;
  return `${xTranslate}px`;
});

const toggleMode = async () => {
  const newMode = isManualMode.value ? 'auto' : 'manual';

  try {
    const timeoutVal =
      inboxTimeout.value === 'never' ? 'never' : inboxTimeout.value;
    await store.dispatch('toggleAgentMode', {
      conversationId: props.conversationId,
      mode: newMode,
      timeout: newMode === 'manual' ? timeoutVal : 'never',
    });
    emit('modeChanged', newMode);
  } catch (error) {
    // Ignore error
  }
};

const toggleTimeoutDropdown = event => {
  event.stopPropagation();
  showTimeoutDropdown.value = !showTimeoutDropdown.value;
};

const selectTimeout = async timeoutValue => {
  showTimeoutDropdown.value = false;

  try {
    await store.dispatch('toggleAgentMode', {
      conversationId: props.conversationId,
      mode: 'manual',
      timeout: timeoutValue,
    });
  } catch (error) {
    // Ignore error
  }
};

const handleClickOutside = event => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showTimeoutDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

// Scroll to selected item when dropdown opens
watch(showTimeoutDropdown, async isVisible => {
  if (isVisible) {
    await nextTick();
    const selectedIndex = timeoutOptions.value.findIndex(
      option => option.value === currentTimeout.value
    );
    if (selectedIndex !== -1 && itemRefs.value[selectedIndex]) {
      itemRefs.value[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }
});

const getItemClass = option => {
  const isSelected = currentTimeout.value === option.value;
  const isNever = option.value === 'never';

  let classes = 'text-slate-700 dark:text-slate-300';

  if (isSelected) {
    classes = isNever
      ? 'text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/20'
      : 'text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20';
  }

  return classes;
};
</script>

<template>
  <div v-if="isAgentBotFeatureEnabled" class="flex items-center gap-2">
    <button
      class="flex items-center w-auto h-8 p-1 transition-all border rounded-full group relative duration-300 ease-in-out z-0"
      :class="
        isManualMode
          ? 'border-n-weak bg-n-alpha-2'
          : 'border-n-blue-9 bg-n-blue-3 dark:bg-n-blue-2'
      "
      @click="toggleMode"
    >
      <div
        ref="autoModeRef"
        class="flex items-center gap-1.5 px-2 z-20 text-sm"
        :class="isManualMode ? '' : 'text-white'"
      >
        <span class="i-lucide-bot size-3.5" />
        {{ $t('AGENT_MODE.AUTO') }}
      </div>
      <div
        ref="manualModeRef"
        class="flex items-center gap-1.5 px-2 z-20 text-sm"
      >
        <span class="i-lucide-user size-3.5" />
        {{ $t('AGENT_MODE.MANUAL') }}
      </div>
      <div
        class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] transition-all duration-300 ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)]"
        :class="isManualMode ? 'bg-n-solid-1' : 'bg-n-blue-9'"
        :style="{
          '--chip-width': width,
          '--translate-x': translateValue,
          '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
        }"
      />
    </button>

    <!-- Timeout Clock Icon - Only shown in manual mode -->
    <div v-if="isManualMode" ref="dropdownRef" class="relative">
      <button
        class="flex items-center justify-center w-8 h-8 rounded-full border transition-all duration-200 hover:opacity-80"
        :class="
          hasScheduledTimeout
            ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
            : 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
        "
        :title="
          hasScheduledTimeout ? 'Timeout scheduled' : 'No timeout (Never)'
        "
        @click="toggleTimeoutDropdown"
      >
        <span class="i-lucide-timer size-4" />
      </button>

      <!-- Timeout Dropdown Menu -->
      <div
        v-if="showTimeoutDropdown"
        class="absolute bottom-full mb-2 right-0 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 py-1 overflow-y-auto max-h-60"
      >
        <div
          class="px-3 py-2 text-xs font-semibold text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700"
        >
          {{ $t('AGENT_MODE.TIMEOUT.LABEL') }}
        </div>
        <button
          v-for="option in timeoutOptions"
          :key="option.value"
          ref="itemRefs"
          class="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center justify-between"
          :class="getItemClass(option)"
          @click="selectTimeout(option.value)"
        >
          {{ option.label }}
          <span
            v-if="currentTimeout === option.value"
            class="i-lucide-check size-4"
          />
        </button>
      </div>
    </div>
  </div>
</template>
